<script setup lang="ts">
  import { UIDatepicker, UITextSmRegular, UIContentWrap } from '@gohighlevel/ghl-ui'
  import { useDocumentStore } from '@/store/document'
  import { PropType, computed, inject, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import {
    DateFieldElementOptions,
    IElement,
    IRecipient,
    Tooltip,
    getSignatureElementColorStyles,
    validateDate,
    DEFAULT_DATE_FORMAT,
    convertToUTC,
    convertUtcToUNIX,
    PageElementOptions,
    DocumentStatus,
    isDatePickerMonthLabelShifted,
    getShiftCorrectedMonthLabels,
  } from '@gohighlevel/ghl-proposals-common'
  import { useDocument, useFillableFields, usePrintAdjust, useTypography } from '@/composable'
  import { useFillableFieldsV2 } from '@/composable/useFillableFieldsV2'
  import { onClickOutside } from '@vueuse/core'

  const props = defineProps({
    page: {
      type: Object as PropType<IElement<PageElementOptions>>,
      required: true,
    },
    element: {
      type: Object as PropType<IElement<DateFieldElementOptions>>,
      required: true,
    },
    pageId: {
      type: String,
      required: true,
    },
    tabIndex: {
      type: Number,
      required: true,
    },
    pageRef: {
      required: false,
      type: Object as () => HTMLElement,
    },
    parentRef: {
      required: false,
      type: Object as () => HTMLElement,
    },
    isParentPage: {
      type: Boolean,
      required: true,
    },
    applyPrintAdjustment: {
      type: Boolean,
      required: false,
      default: true,
    },
  })

  const localParentRef = ref<HTMLElement | undefined>(props.parentRef)
  const localPageRef = ref<HTMLElement | undefined>(props.pageRef)
  const inverseZoomLevel = ref(0)
  const zoomLevel = ref(0)

  const isMobile = computed(() => {
    const windowWidth = window.innerWidth

    if (windowWidth <= 816) return true
    return false
  })
  watch(
    () => props.parentRef,
    newVal => {
      localParentRef.value = newVal
    },
    { immediate: true }
  )
  watch(
    () => props.pageRef,
    newVal => {
      localPageRef.value = newVal
    },
    { immediate: true }
  )

  const showPanel = ref(false)
  const datePanelRef = ref<HTMLElement>()
  const datePickerPanelRef = ref<HTMLElement | null>(null)
  const datePanelPlacement = ref<string | null>(null)
  const datePanelCheckInterval = ref<NodeJS.Timeout | null>(null)

  const adjustPlacementProp = ref(true)
  let panelPollingInterval: ReturnType<typeof setInterval> | null = null

  const clearPanelPollingInterval = () => {
    if (panelPollingInterval) {
      clearInterval(panelPollingInterval)
      panelPollingInterval = null
    }
  }

  /**
   * NOTE: This is a workaround, this logic needs to be removed once ghl-ui is upgraded to a version with naive-ui >= 2.38.2.
   * Workaround for the Naive UI date-picker month-label bug (naive-ui #5702) in
   * behind-UTC timezones — see `isDatePickerMonthLabelShifted` in the date util
   * for the root cause. The month/year quick-jump is teleported to <body> and
   * only opens when its header is clicked, so while the picker is open we re-align
   * the shifted month labels on the frame after each click (the quick-jump has
   * rendered by then). Remove once naive-ui is upgraded to >= 2.38.2.
   */
  const correctQuickJumpMonthLabels = () => {
    document.querySelectorAll<HTMLElement>('.n-date-panel-month-calendar').forEach(calendar => {
      if (calendar.getAttribute('data-tz-fixed')) return
      const columns = calendar.querySelectorAll('.n-date-panel-month-calendar__picker-col')
      // First column is the (unaffected) year list; the last one holds the months.
      const monthColumn = columns[columns.length - 1]
      const items = Array.from(monthColumn?.querySelectorAll<HTMLElement>('.n-date-panel-month-calendar__picker-col-item') ?? [])
      if (items.length < 12) return
      calendar.setAttribute('data-tz-fixed', '1')
      const corrected = getShiftCorrectedMonthLabels(items.map(item => item.textContent))
      items.forEach((item, i) => {
        if (corrected[i] != null && item.textContent !== corrected[i]) item.textContent = corrected[i]
      })
    })
  }

  const handlePickerClick = () => requestAnimationFrame(correctQuickJumpMonthLabels)
  const startMonthLabelFix = () => document.addEventListener('click', handlePickerClick, true)
  const stopMonthLabelFix = () => document.removeEventListener('click', handlePickerClick, true)

  // Setup global event listener to handle outside clicks
  onClickOutside(
    datePanelRef,
    (_e: any) => {
      showPanel.value = false
    },
    {
      capture: false,
      ignore: [`.v-binder-follower-container`, '.v-binder-follower-content'],
    }
  )
  const { position: printPosition, elRef } = usePrintAdjust(props.page, props.element, localPageRef, localParentRef)
  const isPDFView = inject<boolean>('isPDFView')
  const isPreview = inject<boolean>('isPreview')
  const { toCapitalize } = useTypography()
  const store = useDocumentStore()
  const i18nLocale = useI18n()
  const { documentVersion } = useDocument()
  const isCCrecipient = computed(() => store.isCcRecipient)
  const activeRecipient = computed(() => store.activeRecipient)
  const isEditing = computed(() => store.isEditing)
  const { setActiveField, activeElement } = useFillableFields()
  const isActiveTextFieldElemForRecipient = computed(
    () =>
      activeRecipient.value?.id === props.element?.component.options.recipient &&
      activeRecipient.value?.entityName === props.element?.component.options.entityName
  )

  const recipient = computed(() => {
    const recipients = store.document?.recipients || []
    return (
      recipients.find(
        ({ id, entityName }) =>
          id === props.element?.component.options.recipient && entityName === props.element?.component.options.entityName
      ) || ({} as IRecipient)
    )
  })

  const isRecipientHasCompleted = computed(() => {
    return activeRecipient.value && activeRecipient.value?.hasCompleted && isActiveTextFieldElemForRecipient.value
  })

  const documentExpiryEnabled = computed(() => {
    if (store.documentExpiryEnabled) return true

    return false
  })
  const isDocumentExpired = computed(() => {
    if (store.isDocumentExpired) return true

    return false
  })

  const isDocumentDeclined = computed(() => {
    return store.document.status === DocumentStatus.DECLINED
  })

  const disabled = computed(() => {
    return (
      !store.hasPrevRecipientsCompleted ||
      (!isPreview && !isActiveTextFieldElemForRecipient.value && !isRecipientHasCompleted.value) ||
      isCCrecipient.value ||
      (documentExpiryEnabled.value && isDocumentExpired.value) ||
      isDocumentDeclined.value
    )
  })

  const recipientName = computed(() => {
    const { firstName, lastName } = recipient.value
    return toCapitalize(firstName ? `${firstName} ${lastName || ''}` : '')
  })

  const ghostStyles = computed(() => {
    if (props.element?.component.options?.isGhost) {
      const styleMap = props.element?.responsiveStyles.large as any
      const position = styleMap.position
      const scale = styleMap.scale
      const dimensions = styleMap.dimensions
      const stylesArray = []
      if (position) {
        if (props.isParentPage && documentVersion.value === 2) {
          let styleStr = `top: ${position.top}px; `
          styleStr += `left: ${position.left}px;`
          styleStr += 'z-index: 100;'
          stylesArray.push(styleStr)
        } else {
          let styleStr = position?.preferBottom ? `bottom: ${position.bottom}px;` : `top: ${position.top}px; `
          styleStr += position?.preferRight ? `right: ${position.right}px;` : `left: ${position.left}px;`
          styleStr += 'z-index: 100;'
          stylesArray.push(styleStr)
        }
      }
      if (scale) {
        stylesArray.push(`transform: scale(${scale.scaleX}, ${scale.scaleY}) translate3d(0,0,0);`)
      }
      if (dimensions) {
        stylesArray.push(`height: ${dimensions.height}px; width: ${dimensions.width}px;`)
      }
      stylesArray.push('position: absolute;  display: flex;')
      return stylesArray.join(' ').trim()
    }
    return ''
  })

  const { addElementRef } = useFillableFieldsV2()
  onMounted(() => {
    if (store.isAlreadyAccepted) {
      store.updateFillableElement(props.element?.id, props.pageId, props.element?.component.options.text as string, true)
    }

    // Required to ensure the element is added to the map and rendered else PDF page will not have this element
    nextTick(() => {
      if (elRef.value) {
        addElementRef(props.element?.id as string, elRef.value)
      }

      // Set autocomplete="off" on the actual input element inside the date picker
      const datePickerId = `floating_date_field-${props.element?.id}`
      const setAutocompleteOff = () => {
        const datePickerElement = document.getElementById(datePickerId)
        if (datePickerElement) {
          const inputElement = datePickerElement.querySelector('input.n-input__input-el') as HTMLInputElement
          if (inputElement) {
            inputElement.setAttribute('autocomplete', 'off')
            return true
          }
        }
        return false
      }

      setAutocompleteOff()
    })
  })
  const onUpdateTextField = (text: string) => {
    const timestamp = convertToUTC(text)
    store.updateFillableFieldForRecipient(recipient.value.id, timestamp as string, props.element?.id as string, timestamp as string)
    store.updateFillableElement(props.element?.id, props.pageId, timestamp as string, true)
    showPanel.value = false
  }
  const styles = computed(() => getSignatureElementColorStyles(recipient.value))
  const dimensions = computed(() => {
    const dimensionsObj = props.element?.responsiveStyles.large.dimensions
    return (
      {
        height: dimensionsObj?.height + 'px',
        width: dimensionsObj?.width + 'px',
      } || { height: 'auto', width: 'auto' }
    )
  })
  const commonStyles = computed(() => {
    return {
      ...dimensions.value,
      border: '0px solid transparent !important',
    }
  })
  const colorStyles = computed(() => {
    if (isPDFView) {
      return {
        ...commonStyles.value,
        '--n-color-disabled': 'transparent !important',
        backgroundColor: 'transparent !important',
      }
    }
    if (disabled.value) {
      return {
        ...commonStyles.value,
        // border: '0px solid transparent !important',
        'box-shadow': 'none !important',
        '--n-border-hover': '0px solid transparent !important',
        backgroundColor: 'transparent !important',
        '--n-border-disabled': '0px solid transparent !important',
        border: `0px solid transparent !important`,
      }
    }

    return {
      ...styles.value,
      ...commonStyles.value,
      border: `1px solid ${styles.value.borderColor} !important`,
    }
  })

  const onClick = () => {
    datePanelPlacement.value = null
    datePickerPanelRef.value = null

    if (
      !isEditing.value &&
      isActiveTextFieldElemForRecipient.value &&
      !activeRecipient.value?.hasCompleted &&
      isCCrecipient.value === false
    ) {
      store.setIsEditing(true)
    }
    setActiveField(props.element?.id as string)

    if (store.isEditing) {
      showPanel.value = true
      nextTick(() => {
        waitForDatePanelAndScale()
        nextTick(() => {
          applyZoomToDatePanel()
        })
      })
    }

    window.visualViewport?.removeEventListener('resize', applyZoomToDatePanel)
    document.getElementById('document-pages-container')?.removeEventListener('scroll', applyZoomToDatePanel)
    window.visualViewport?.addEventListener('resize', applyZoomToDatePanel)
    document.getElementById('document-pages-container')?.addEventListener('scroll', applyZoomToDatePanel)
  }

  // Create a fixed number of zoom buckets (e.g. every 0.1 step from 1 to 4.5)
  const scaleMap: Record<number, number> = {}
  for (let z = 1; z <= 4.5; z += 0.1) {
    const rounded = parseFloat(z.toFixed(1)) // avoids floating point noise
    scaleMap[rounded] = 1 / rounded // or tweak to `1 / Math.pow(rounded, 0.9)` for smoother feel
  }

  function getScaleLevel(zoom: number): number {
    if (zoom <= 1) return 1

    // Round zoom to nearest 0.1 to match a bucket
    const bucket = parseFloat((Math.round(zoom * 10) / 10).toFixed(1))

    return scaleMap[bucket] ?? 1 / zoom
  }

  /**
   * @param placement - v-placement attribute value
   * @returns - transform-origin value to give the correct position of the date panel during zoom/scaling
   */
  function getTransformOriginFromPlacement(placement: string): string {
    if (placement === 'top' || placement === 'bottom') {
      return `${placement === 'top' ? 'bottom' : 'top'} center`
    }
    if (placement === 'left' || placement === 'right') {
      return `center ${placement === 'left' ? 'left' : 'right'}`
    }

    const verticalPlacement = placement.includes('top') ? 'bottom' : 'top'
    const horizontalPlacement = placement.includes('start') ? 'left' : 'right'
    return `${verticalPlacement} ${horizontalPlacement}`
  }

  function applyZoomToDatePanel() {
    const zoom = window.visualViewport?.scale || 1
    zoomLevel.value = zoom
    inverseZoomLevel.value = 1 / zoom

    const currentPanelPlacement = datePickerPanelRef.value?.parentElement?.getAttribute('v-placement') || 'bottom'

    //Only get panel reference if it is not already set or if the panel placement has changed, placement can change on scroll
    if (!datePickerPanelRef.value || currentPanelPlacement !== datePanelPlacement.value) {
      const panel = document.querySelector('.n-date-panel') as HTMLElement | null
      if (panel) {
        datePickerPanelRef.value = panel
      } else {
        return
      }
    }

    // We are using adjust-placement prop only for the first time when the date panel is rendered
    datePanelPlacement.value = datePickerPanelRef.value?.parentElement?.getAttribute('v-placement') || 'bottom'
    adjustPlacementProp.value = false
    datePickerPanelRef.value?.parentElement?.setAttribute('datepicker-id', props.element?.id)

    // datePickerPanelRef.value.style.zoom = getScaleLevel(zoom)
    datePickerPanelRef.value.style.transformOrigin = getTransformOriginFromPlacement(datePanelPlacement.value || 'bottom')
    datePickerPanelRef.value.style.transform = `scale(${getScaleLevel(zoom)})`
    // datePickerPanelRef.value.style.position = 'fixed'
    datePickerPanelRef.value.style.top = '0px'
    datePickerPanelRef.value.style.left = '0px'
  }

  // Date panel is available only after some time from the click
  function waitForDatePanelAndScale() {
    clearPanelPollingInterval()
    panelPollingInterval = setInterval(() => {
      const panel = document.querySelector('.n-date-panel') as HTMLElement | null
      if (panel) {
        const checkPanelIsForAnotherElement = panel.parentElement?.getAttribute('datepicker-id')
        if (checkPanelIsForAnotherElement) {
          return
        }
        clearPanelPollingInterval()
        datePickerPanelRef.value = panel
      }
    }, 50)
  }

  watch(showPanel, newValue => {
    if (newValue) {
      if (isDatePickerMonthLabelShifted()) startMonthLabelFix()
      return
    }
    if (!newValue) {
      window.visualViewport?.removeEventListener('resize', applyZoomToDatePanel)
      document.getElementById('page-renderer')?.removeEventListener('scroll', applyZoomToDatePanel)
      if (datePanelCheckInterval.value) {
        clearInterval(datePanelCheckInterval.value)
        datePanelCheckInterval.value = null
      }
      datePickerPanelRef.value = null
      adjustPlacementProp.value = true
      clearPanelPollingInterval()
    }
  })

  onUnmounted(() => {
    window.visualViewport?.removeEventListener('resize', applyZoomToDatePanel)
    document.getElementById('document-pages-container')?.removeEventListener('scroll', applyZoomToDatePanel)
    if (datePanelCheckInterval.value) {
      clearInterval(datePanelCheckInterval.value)
      datePanelCheckInterval.value = null
    }
    datePickerPanelRef.value = null
    adjustPlacementProp.value = true
    clearPanelPollingInterval()
  })
</script>

<template>
  <div
    ref="elRef"
    class="date-field-el"
    :class="{
      filled: props.element?.component.options.text,
      'apply-print-adjustment': applyPrintAdjustment,
    }"
    :style="ghostStyles"
    :id="props.element?.id"
    :data-attr="props.element?.component.options.text ? convertUtcToUNIX(props.element?.component.options.text) : null"
  >
    <Tooltip direction="bottom" :enable="true">
      <template #trigger>
        <div class="print:break-inside-avoid !break-inside-avoid box-border date-field-el-wrapper" @click="onClick">
          <span v-if="element?.component.options.required && !disabled && !isPDFView && !isRecipientHasCompleted" class="option--required">
            {{ $t('common.requiredIndicator') }}
          </span>
          <UIContentWrap :locale="i18nLocale?.locale?.value || 'en-US'" class="p-0" style="display: contents">
            <UIDatepicker
              :tabindex="tabIndex"
              @on-focus="onClick"
              ref="datePanelRef"
              :show="showPanel"
              :auto-complete-off="true"
              :class="{
                'self-completed': props.element?.component.options.text && isRecipientHasCompleted,
                completed: props.element?.component.options.text && !isActiveTextFieldElemForRecipient,
                'hidden-suffix':
                  isPDFView || (props.element?.component.options.text && !isActiveTextFieldElemForRecipient) || isRecipientHasCompleted,
                'no-active': isPDFView ? true : props.element?.component.options.text || isRecipientHasCompleted,
                'active-el': isPDFView ? true : !isRecipientHasCompleted,
                '!border-2 !border-error-500': element?.component.options.textFieldErrorMessage,
              }"
              class="date-field-input rounded-sm !text-sm"
              :style="{
                ...(!props.element?.component.options.text ? colorStyles : commonStyles),
                'border-width': activeElement && activeElement.id === props.element?.id ? '0px' : '1px',
              }"
              :id="`floating_date_field-${props.element?.id}`"
              :placeholder="props.element?.component.options.placeholder"
              :value="props.element?.component.options.text ? convertUtcToUNIX(props.element?.component.options.text) : null"
              :format="props.element?.component.options.dateFormat || DEFAULT_DATE_FORMAT"
              type="date"
              :disabled="disabled"
              :is-date-disabled="
                (timestamp: number) =>
                  !isRecipientHasCompleted && activeRecipient?.id === props.element?.component.options.recipient
                    ? validateDate(props.element?.component.options.availableDates, timestamp)
                    : false
              "
              @update:value="val => onUpdateTextField(val)"
              size="medium"
              :actions="['now', 'clear']"
              :input-readonly="isMobile"
              :to="isMobile ? '#page-renderer' : 'body'"
              :adjust-placement="adjustPlacementProp"
            />
          </UIContentWrap>
        </div>
      </template>
      <template #content>
        <UITextSmRegular class="capitalize font-semibold px-2 py-1 text-xs" :style="{ color: styles.placeholderColor }">
          {{ recipientName }}
        </UITextSmRegular>
      </template>
    </Tooltip>
  </div>
</template>

<style lang="scss">
  .date-field-input {
    .n-input {
      --n-color-disabled: #f1f1f1 !important;
      --n-text-color-disabled: #333 !important;
      --n-color-focus: v-bind('styles.backgroundColor') !important;
      --n-height: v-bind('dimensions.height') !important;
      --n-border-hover: 1px solid v-bind('styles.borderColor') !important;
      --n-border-pressed: 1px solid transparent !important;
      --n-border-focus: 1px solid v-bind('styles.borderColor') !important;
      --n-caret-color: 1px solid transparent !important;
      --n-box-shadow-focus: 1px solid transparent !important;
      --n-loading-color: 1px solid transparent !important;

      --n-border: 1px solid transparent !important;
      --n-color: transparent !important;
      box-shadow: none !important;
      border-radius: 2px;
      .n-input-wrapper {
        padding: 0 !important;
        .n-input__suffix {
          .n-base-icon {
            color: rgb(141, 141, 141) !important;
          }
        }
        .n-input__input-el {
          padding: 0px 2px !important;
          font-weight: 600 !important;
        }
        .n-input__placeholder {
          padding: 0px 2px !important;
          color: v-bind('styles.placeholderColor') !important;
        }
      }
      .n-input__border {
        border: 1px solid transparent;
      }
      &.n-input--disabled {
        cursor: default !important;
        .n-input__placeholder {
          color: #c8c8c8 !important;
        }
        .n-input__suffix {
          .n-base-icon {
            color: #c8c8c8 !important;
          }
        }
      }
    }
    &.hidden-suffix {
      .n-input-wrapper {
        .n-input__suffix {
          visibility: hidden;
        }
      }
    }
    &.completed,
    &.self-completed {
      pointer-events: none !important;
      .n-input.n-input--disabled {
        --n-color-disabled: transparent !important;
      }
    }
  }

  .option--required {
    position: absolute;
    right: -7px;
    top: -17px;
    color: red;
    font-size: 22px;
    font-weight: 700;
  }
  .date-field {
    .n-input__input {
      font-size: 14px !important;
    }
  }

  @media print {
    .date-field-el {
      break-inside: avoid-page !important;
      // transform: v-bind('printPosition.top') !important;
      &.apply-print-adjustment {
        transform: v-bind('printPosition.top') !important;
      }
    }
  }
</style>
